import { useEffect, useState } from 'react';

import type { TMyAssignmentCourseItem } from '@crehana/compass.dashlets/my-assignment-courses-widget';
import { toast } from '@crehana/compass.ui/toaster';
import { useTranslation } from '@crehana/i18n';

import { useGetMyAssignedCoursesQueryQuery } from './graphql/get-my-assigned-courses-query.v5.generated';
import { getAssignedCoursesMap, getNodeData } from './utils';

type TPageInfo = {
  endCursor: string;
  hasNextPage: boolean;
};

type TUseMyAssignedCoursesQueryProps = {
  pageSize?: number;
  search?: string;
};

const DEFAULT_PAGE_SIZE = 5;
const ERROR_TYPES = [
  'FoundationHomeUserIntegrityError',
  'FoundationHomeUserInternalError',
];

export const useMyAssignedCoursesQuery = (
  props: TUseMyAssignedCoursesQueryProps,
) => {
  const { t } = useTranslation();
  const [loadingMore, setLoadingMore] = useState(false);
  const [courses, setCourses] = useState<TMyAssignmentCourseItem[]>([]);
  const [pageInfo, setPageInfo] = useState<TPageInfo | undefined>(undefined);

  const {
    data,
    loading,
    error: errorQuery,
    refetch,
    fetchMore,
  } = useGetMyAssignedCoursesQueryQuery({
    variables: {
      input: {
        pagination: { first: props.pageSize || DEFAULT_PAGE_SIZE },
        search: { query: props.search },
      },
    },
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'v5' },
    onError: () => toast.error(t('ERROR_FETCHING_COURSES')),
  });

  const error =
    ERROR_TYPES.includes(
      data?.foundation_home_user?.get_my_assigned_courses?.__typename ?? '',
    ) || !!errorQuery;

  const fetchMoreCourses = async () => {
    if (!pageInfo?.hasNextPage) {
      return;
    }

    try {
      setLoadingMore(true);
      const { data: dataMore } = await fetchMore({
        variables: {
          input: {
            pagination: {
              first: props.pageSize || DEFAULT_PAGE_SIZE,
              after: pageInfo?.endCursor,
            },
            search: { query: props.search },
          },
        },
      });

      const nodeData = getNodeData(dataMore);
      const coursesMap = getAssignedCoursesMap(dataMore);

      setCourses(prev => [...prev, ...coursesMap]);
      setPageInfo({
        endCursor: nodeData?.page_info?.end_cursor || '',
        hasNextPage: nodeData?.page_info?.has_next_page || false,
      });
    } catch (error) {
      toast.error(t('ERROR_FETCHING_COURSES'));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const nodeData = getNodeData(data);
    const coursesMap = getAssignedCoursesMap(data);

    setCourses(coursesMap);
    setPageInfo({
      endCursor: nodeData?.page_info?.end_cursor || '',
      hasNextPage: nodeData?.page_info?.has_next_page || false,
    });
  }, [data]);

  return {
    loading,
    loadingMore,
    courses,
    error,
    coursesStarted: getNodeData(data)?.courses_started || undefined,
    refetch,
    fetchMoreCourses,
  };
};
